/**
 * EOS Headlines - Horizontal Slider by Person (Slack-style)
 * 
 * Features:
 * - Horizontal scrollable columns by user
 * - Card-based display with category badges
 * - Modal-based add/edit (no side panels)
 * - Inline "+ Add Headline" per person column
 * - Tabs filter by category (All, Customer Win, Milestone, Recognition, Rock Complete, General)
 * - Drag-and-drop reordering within user columns
 */

import React, { useState, useEffect, useRef } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Megaphone, 
  Plus, 
  ChevronLeft, 
  ChevronRight,
  Trophy,
  Target,
  Award,
  ThumbsUp,
  MessageSquare,
  Trash2,
  ExternalLink,
  Calendar,
  Heart,
  GripVertical
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import UserAvatar from '../components/UserAvatar';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category configuration
const CATEGORY_CONFIG = {
  customer_win: { 
    label: 'Customer Win', 
    color: 'bg-green-500', 
    icon: Trophy, 
    textColor: 'text-green-500',
    bgLight: 'bg-green-100 dark:bg-green-950/30'
  },
  milestone: { 
    label: 'Milestone', 
    color: 'bg-blue-500', 
    icon: Target, 
    textColor: 'text-blue-500',
    bgLight: 'bg-blue-100 dark:bg-blue-950/30'
  },
  recognition: { 
    label: 'Recognition', 
    color: 'bg-purple-500', 
    icon: Award, 
    textColor: 'text-purple-500',
    bgLight: 'bg-purple-100 dark:bg-purple-950/30'
  },
  rock_complete: { 
    label: 'Rock Complete', 
    color: 'bg-orange-500', 
    icon: ThumbsUp, 
    textColor: 'text-orange-500',
    bgLight: 'bg-orange-100 dark:bg-orange-950/30'
  },
  general: { 
    label: 'General', 
    color: 'bg-slate-500', 
    icon: Megaphone, 
    textColor: 'text-slate-400',
    bgLight: 'bg-slate-100 dark:bg-slate-800/50'
  },
};

// Headline Card Component (Base - used by SortableHeadlineCard)
const HeadlineCardContent = ({ headline, onClick, isDragging }) => {
  const category = CATEGORY_CONFIG[headline.category] || CATEGORY_CONFIG.general;
  const CategoryIcon = category.icon;
  const reactionCount = headline.reactions?.length || 0;
  const commentCount = headline.comments?.length || 0;
  
  return (
    <Card 
      className={`dark:bg-slate-700/50 border-slate-600 hover:border-blue-400 cursor-pointer transition-all hover:shadow-lg group ${isDragging ? 'shadow-xl border-blue-500 ring-2 ring-blue-400/50' : ''}`}
      onClick={() => !isDragging && onClick(headline)}
      data-testid={`headline-card-${headline.id}`}
    >
      <CardContent className="p-3">
        {/* Category Badge */}
        <div className="flex items-center justify-between mb-2">
          <Badge 
            variant="outline" 
            className={`${category.color} text-white border-0 text-xs`}
          >
            <CategoryIcon className="h-3 w-3 mr-1" />
            {category.label}
          </Badge>
          {headline.department && (
            <span className="text-[10px] text-slate-400">{headline.department}</span>
          )}
        </div>
        
        {/* Message Preview */}
        <p className="text-sm font-medium dark:text-white mb-2 line-clamp-2">
          {headline.message}
        </p>
        
        {/* Date */}
        <div className="flex items-center gap-1 text-xs text-slate-400 mb-2">
          <Calendar className="h-3 w-3" />
          <span>{format(new Date(headline.created_at), 'MMM d, yyyy')}</span>
        </div>
        
        {/* Footer with reactions and comments */}
        <div className="flex items-center gap-3 pt-2 border-t border-slate-600/50 text-xs text-slate-400">
          {reactionCount > 0 && (
            <span className="flex items-center gap-1">
              <Heart className="h-3 w-3" />
              {reactionCount}
            </span>
          )}
          {commentCount > 0 && (
            <span className="flex items-center gap-1">
              <MessageSquare className="h-3 w-3" />
              {commentCount}
            </span>
          )}
          {headline.link_url && (
            <ExternalLink className="h-3 w-3" />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Sortable Headline Card Component with drag handle
const SortableHeadlineCard = ({ headline, onClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: headline.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group/card">
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className="absolute left-1 top-1/2 -translate-y-1/2 z-10 cursor-grab active:cursor-grabbing text-slate-500 hover:text-slate-300 opacity-0 group-hover/card:opacity-100 transition-opacity bg-slate-800/80 rounded p-1"
        onClick={(e) => e.stopPropagation()}
        data-testid={`drag-handle-${headline.id}`}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <HeadlineCardContent headline={headline} onClick={onClick} isDragging={isDragging} />
    </div>
  );
};

// Person Column Component with Sortable Context
const PersonColumn = ({ user, headlines, onAddHeadline, onHeadlineClick, defaultCategory, onReorderHeadlines }) => {
  const userHeadlines = headlines.filter(h => h.author_user_id === user.id);
  const headlineIds = userHeadlines.map(h => h.id);
  
  return (
    <div className="flex-shrink-0 w-72 bg-slate-800/40 rounded-lg border border-slate-700" data-testid={`person-column-${user.id}`}>
      {/* Column Header */}
      <div className="p-3 border-b border-slate-700 sticky top-0 bg-slate-800/90 backdrop-blur-sm rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserAvatar user={user} size="sm" />
            <div>
              <p className="font-medium text-sm dark:text-white">
                {user.first_name} {user.last_name}
              </p>
              <p className="text-xs text-slate-400">
                {userHeadlines.length} headline{userHeadlines.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Headlines List with Sortable Context */}
      <div className="p-2 space-y-2 max-h-[calc(100vh-320px)] overflow-y-auto scrollbar-thin">
        <SortableContext items={headlineIds} strategy={verticalListSortingStrategy}>
          {userHeadlines.map(headline => (
            <SortableHeadlineCard 
              key={headline.id} 
              headline={headline} 
              onClick={onHeadlineClick}
            />
          ))}
        </SortableContext>
        
        {userHeadlines.length === 0 && (
          <p className="text-center text-slate-500 text-sm py-4">No headlines shared</p>
        )}
        
        {/* Add Headline Button */}
        <Button
          variant="ghost"
          className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-700/50 border border-dashed border-slate-600"
          onClick={() => onAddHeadline(user, defaultCategory)}
          data-testid={`add-headline-btn-${user.id}`}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Headline
        </Button>
      </div>
    </div>
  );
};

const EOSHeadlinesSlider = () => {
  const [headlines, setHeadlines] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [viewMode, setViewMode] = useState('my'); // 'my' or 'all'
  const scrollContainerRef = useRef(null);
  const [activeId, setActiveId] = useState(null);
  
  // Get user info from AuthContext for role-based filtering
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser && ['super_admin', 'admin', 'ceo', 'president'].includes(currentUser.role);
  const currentUserId = currentUser?.user_id || currentUser?.id;
  
  // Drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // Modal States
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedHeadline, setSelectedHeadline] = useState(null);
  const [prefilledUser, setPrefilledUser] = useState(null);
  
  // Comment state
  const [commentText, setCommentText] = useState('');
  
  // Form State
  const [headlineForm, setHeadlineForm] = useState({
    message: '',
    category: 'general',
    department: '',
    image_url: '',
    link_url: ''
  });

  useEffect(() => {
    fetchHeadlines();
    fetchUsers();
  }, [viewMode]);

  const fetchHeadlines = async () => {
    try {
      let url = `${API}/eos/headlines`;
      // If "My Headlines" mode, filter by current user
      if (viewMode === 'my' && currentUserId) {
        url += `?author_id=${currentUserId}`;
      }
      const response = await axios.get(url);
      setHeadlines(response.data);
    } catch (error) {
      console.error('Error fetching headlines:', error);
      toast.error('Failed to load headlines');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      const activeUsers = response.data.filter(u => u.is_active !== false);
      setUsers(activeUsers.slice(0, 15));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleAddHeadline = (user, defaultCategory = 'general') => {
    setPrefilledUser(user);
    setHeadlineForm({
      message: '',
      category: categoryFilter !== 'all' ? categoryFilter : defaultCategory,
      department: '',
      image_url: '',
      link_url: ''
    });
    setCreateDialogOpen(true);
  };

  const handleCreateHeadline = async () => {
    try {
      if (!headlineForm.message.trim()) {
        toast.error('Please enter a message');
        return;
      }

      // Note: Backend will set author_user_id from current user token
      await axios.post(`${API}/eos/headlines`, headlineForm);
      toast.success('Headline shared successfully!');
      setCreateDialogOpen(false);
      resetHeadlineForm();
      fetchHeadlines();
    } catch (error) {
      console.error('Error creating headline:', error);
      toast.error(error.response?.data?.detail || 'Failed to share headline');
    }
  };

  const handleHeadlineClick = (headline) => {
    setSelectedHeadline(headline);
    setViewDialogOpen(true);
  };

  const handleReact = async (emoji) => {
    if (!selectedHeadline) return;
    
    try {
      await axios.post(`${API}/eos/headlines/${selectedHeadline.id}/react`, null, {
        params: { emoji }
      });
      fetchHeadlines();
      // Update selected headline
      const updated = await axios.get(`${API}/eos/headlines`);
      const found = updated.data.find(h => h.id === selectedHeadline.id);
      if (found) setSelectedHeadline(found);
    } catch (error) {
      console.error('Error reacting:', error);
      toast.error('Failed to add reaction');
    }
  };

  const handleComment = async () => {
    if (!selectedHeadline || !commentText.trim()) return;
    
    try {
      await axios.post(
        `${API}/eos/headlines/${selectedHeadline.id}/comment`,
        null,
        { params: { text: commentText } }
      );
      
      toast.success('Comment added');
      setCommentText('');
      fetchHeadlines();
      // Update selected headline
      const updated = await axios.get(`${API}/eos/headlines`);
      const found = updated.data.find(h => h.id === selectedHeadline.id);
      if (found) setSelectedHeadline(found);
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    }
  };

  const handleDelete = async () => {
    if (!selectedHeadline) return;
    
    try {
      await axios.delete(`${API}/eos/headlines/${selectedHeadline.id}`);
      toast.success('Headline deleted');
      setDeleteDialogOpen(false);
      setViewDialogOpen(false);
      setSelectedHeadline(null);
      fetchHeadlines();
    } catch (error) {
      console.error('Error deleting headline:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete headline');
    }
  };

  const resetHeadlineForm = () => {
    setHeadlineForm({
      message: '',
      category: 'general',
      department: '',
      image_url: '',
      link_url: ''
    });
    setPrefilledUser(null);
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  // Drag and drop handlers
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    
    if (!over || active.id === over.id) return;

    // Find the headlines being moved
    const activeHeadline = headlines.find(h => h.id === active.id);
    const overHeadline = headlines.find(h => h.id === over.id);
    
    if (!activeHeadline || !overHeadline) return;
    
    // Only allow reordering within the same user's column
    if (activeHeadline.author_user_id !== overHeadline.author_user_id) {
      toast.info('Headlines can only be reordered within the same person\'s column');
      return;
    }

    // Get headlines for this user and reorder
    const userHeadlines = headlines.filter(h => h.author_user_id === activeHeadline.author_user_id);
    const otherHeadlines = headlines.filter(h => h.author_user_id !== activeHeadline.author_user_id);
    
    const oldIndex = userHeadlines.findIndex(h => h.id === active.id);
    const newIndex = userHeadlines.findIndex(h => h.id === over.id);
    
    const reorderedUserHeadlines = arrayMove(userHeadlines, oldIndex, newIndex);
    
    // Update display order for the reordered headlines
    const updatedHeadlines = reorderedUserHeadlines.map((h, index) => ({
      ...h,
      display_order: index
    }));
    
    // Combine with other headlines
    setHeadlines([...otherHeadlines, ...updatedHeadlines]);
    
    // Show success feedback
    toast.success('Headline order updated');
    
    // Persist the order to backend (optional - you can add an API endpoint for this)
    // For now, order is maintained in frontend state only
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  // Get the active headline for drag overlay
  const activeHeadline = activeId ? headlines.find(h => h.id === activeId) : null;

  // Filter headlines based on category
  const filteredHeadlines = categoryFilter === 'all' 
    ? headlines 
    : headlines.filter(h => h.category === categoryFilter);

  // Calculate stats
  const stats = {
    total: headlines.length,
    customer_win: headlines.filter(h => h.category === 'customer_win').length,
    milestone: headlines.filter(h => h.category === 'milestone').length,
    recognition: headlines.filter(h => h.category === 'recognition').length,
    rock_complete: headlines.filter(h => h.category === 'rock_complete').length,
    general: headlines.filter(h => h.category === 'general').length,
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading Headlines...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-4" data-testid="headlines-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Megaphone className="h-8 w-8 text-green-500" />
            <div>
              <h1 className="text-2xl font-bold dark:text-white">Headlines</h1>
              <p className="text-sm text-muted-foreground">
                Share and celebrate good news from the team
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <div className="flex rounded-lg border border-slate-600 overflow-hidden" data-testid="view-mode-toggle">
              <button
                onClick={() => setViewMode('my')}
                className={`px-3 py-1.5 text-sm transition-colors ${
                  viewMode === 'my' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
                data-testid="view-mode-my"
              >
                My Headlines
              </button>
              {isAdmin && (
                <button
                  onClick={() => setViewMode('all')}
                  className={`px-3 py-1.5 text-sm transition-colors ${
                    viewMode === 'all' 
                      ? 'bg-green-600 text-white' 
                      : 'bg-slate-800 text-slate-400 hover:text-white'
                  }`}
                  data-testid="view-mode-all"
                >
                  All Headlines
                </button>
              )}
            </div>
            
            <Button onClick={() => handleAddHeadline(null)} data-testid="add-headline-btn">
              <Plus className="h-4 w-4 mr-2" />
              Share Good News
            </Button>
          </div>
        </div>

        {/* Category Filter Tabs */}
        <Tabs value={categoryFilter} onValueChange={setCategoryFilter} className="w-full">
          <TabsList className="bg-slate-800/40">
            <TabsTrigger value="all" data-testid="tab-all">All ({stats.total})</TabsTrigger>
            <TabsTrigger value="customer_win" data-testid="tab-customer-win">
              <Trophy className="h-3 w-3 mr-1" />
              Customer Wins ({stats.customer_win})
            </TabsTrigger>
            <TabsTrigger value="milestone" data-testid="tab-milestone">
              <Target className="h-3 w-3 mr-1" />
              Milestones ({stats.milestone})
            </TabsTrigger>
            <TabsTrigger value="recognition" data-testid="tab-recognition">
              <Award className="h-3 w-3 mr-1" />
              Recognition ({stats.recognition})
            </TabsTrigger>
            <TabsTrigger value="rock_complete" data-testid="tab-rock-complete">
              <ThumbsUp className="h-3 w-3 mr-1" />
              Rock Complete ({stats.rock_complete})
            </TabsTrigger>
            <TabsTrigger value="general" data-testid="tab-general">
              <Megaphone className="h-3 w-3 mr-1" />
              General ({stats.general})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Stats Bar */}
        <div className="flex gap-4 p-3 bg-slate-800/40 rounded-lg border border-slate-700">
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm">Total:</span>
            <Badge variant="outline" className="dark:bg-slate-700">{filteredHeadlines.length}</Badge>
          </div>
          {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
            const count = filteredHeadlines.filter(h => h.category === key).length;
            if (count === 0 && categoryFilter !== 'all' && categoryFilter !== key) return null;
            const Icon = config.icon;
            return (
              <div key={key} className="flex items-center gap-2">
                <Icon className={`h-4 w-4 ${config.textColor}`} />
                <Badge className={config.color}>{count}</Badge>
              </div>
            );
          })}
        </div>

        {/* Horizontal Slider Container with Drag and Drop */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div className="relative">
            {/* Scroll Buttons */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-slate-800/80 hover:bg-slate-700 rounded-full shadow-lg"
              onClick={scrollLeft}
              data-testid="scroll-left-btn"
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-slate-800/80 hover:bg-slate-700 rounded-full shadow-lg"
              onClick={scrollRight}
              data-testid="scroll-right-btn"
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
            
            {/* Scrollable Container */}
            <div 
              ref={scrollContainerRef}
              className="flex gap-4 overflow-x-auto px-10 pb-4 scrollbar-thin"
              style={{ scrollbarWidth: 'thin' }}
              data-testid="headlines-slider"
            >
              {(viewMode === 'my' 
                ? users.filter(u => u.id === currentUserId)
                : users
              ).map(user => (
                <PersonColumn
                  key={user.id}
                  user={user}
                  headlines={filteredHeadlines}
                  onAddHeadline={handleAddHeadline}
                  onHeadlineClick={handleHeadlineClick}
                  defaultCategory={categoryFilter !== 'all' ? categoryFilter : 'general'}
                />
              ))}
              
              {users.length === 0 && (
                <div className="flex-1 flex items-center justify-center p-8 text-slate-500">
                  No team members found.
                </div>
              )}
            </div>
          </div>
          
          {/* Drag Overlay - shows the card being dragged */}
          <DragOverlay>
            {activeHeadline && (
              <div className="w-72 opacity-90">
                <HeadlineCardContent 
                  headline={activeHeadline} 
                  onClick={() => {}} 
                  isDragging={true}
                />
              </div>
            )}
          </DragOverlay>
        </DndContext>

        {/* Empty State for filtered view */}
        {filteredHeadlines.length === 0 && categoryFilter !== 'all' && (
          <div className="text-center py-8 text-slate-500">
            <Megaphone className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No {CATEGORY_CONFIG[categoryFilter]?.label} headlines yet.</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => handleAddHeadline(null)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Share the first one!
            </Button>
          </div>
        )}

        {/* Floating Action Button - Quick Add */}
        <button
          onClick={() => handleAddHeadline(null)}
          className="fixed bottom-8 right-8 w-14 h-14 bg-green-600 hover:bg-green-500 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center z-50 group"
          data-testid="fab-add-headline"
          title="Share Good News"
        >
          <Plus className="h-6 w-6 group-hover:rotate-90 transition-transform duration-200" />
        </button>
      </div>

      {/* Create Headline Modal */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="dark:text-white flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-green-500" />
              Share Good News
            </DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              {prefilledUser && (
                <span>Creating headline for {prefilledUser.first_name} {prefilledUser.last_name}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="dark:text-slate-200">Message *</Label>
              <Textarea
                value={headlineForm.message}
                onChange={(e) => setHeadlineForm({ ...headlineForm, message: e.target.value })}
                placeholder="What's the good news?"
                className="dark:bg-slate-700 dark:border-slate-600"
                rows={4}
                data-testid="headline-message-input"
              />
            </div>
            
            <div>
              <Label className="dark:text-slate-200">Category</Label>
              <Select 
                value={headlineForm.category} 
                onValueChange={(val) => setHeadlineForm({ ...headlineForm, category: val })}
              >
                <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600" data-testid="headline-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                  <SelectItem value="general">
                    <div className="flex items-center gap-2">
                      <Megaphone className="h-4 w-4" />
                      General
                    </div>
                  </SelectItem>
                  <SelectItem value="customer_win">
                    <div className="flex items-center gap-2">
                      <Trophy className="h-4 w-4 text-green-500" />
                      Customer Win
                    </div>
                  </SelectItem>
                  <SelectItem value="milestone">
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-blue-500" />
                      Milestone
                    </div>
                  </SelectItem>
                  <SelectItem value="recognition">
                    <div className="flex items-center gap-2">
                      <Award className="h-4 w-4 text-purple-500" />
                      Recognition
                    </div>
                  </SelectItem>
                  <SelectItem value="rock_complete">
                    <div className="flex items-center gap-2">
                      <ThumbsUp className="h-4 w-4 text-orange-500" />
                      Rock Complete
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="dark:text-slate-200">Department (Optional)</Label>
              <Input
                value={headlineForm.department}
                onChange={(e) => setHeadlineForm({ ...headlineForm, department: e.target.value })}
                placeholder="e.g., Sales, Marketing, Operations"
                className="dark:bg-slate-700 dark:border-slate-600"
              />
            </div>
            
            <div>
              <Label className="dark:text-slate-200">Link URL (Optional)</Label>
              <Input
                value={headlineForm.link_url}
                onChange={(e) => setHeadlineForm({ ...headlineForm, link_url: e.target.value })}
                placeholder="https://..."
                className="dark:bg-slate-700 dark:border-slate-600"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialogOpen(false); resetHeadlineForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleCreateHeadline} data-testid="create-headline-btn">
              Share Headline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View/Edit Headline Modal */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="dark:text-white flex items-center gap-2">
              {selectedHeadline && (
                <>
                  {React.createElement(CATEGORY_CONFIG[selectedHeadline.category]?.icon || Megaphone, {
                    className: `h-5 w-5 ${CATEGORY_CONFIG[selectedHeadline.category]?.textColor || 'text-slate-400'}`
                  })}
                  {CATEGORY_CONFIG[selectedHeadline.category]?.label || 'Headline'}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedHeadline && (
            <div className="space-y-4 py-4">
              {/* Category Badge */}
              <Badge 
                className={`${CATEGORY_CONFIG[selectedHeadline.category]?.color || 'bg-slate-500'} text-white`}
              >
                {CATEGORY_CONFIG[selectedHeadline.category]?.label || 'General'}
              </Badge>
              
              {/* Message */}
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <p className="text-base dark:text-white whitespace-pre-wrap">{selectedHeadline.message}</p>
              </div>
              
              {/* Metadata */}
              <div className="flex items-center justify-between text-sm text-slate-400">
                <span>By {selectedHeadline.author_name}</span>
                <span>{format(new Date(selectedHeadline.created_at), 'MMM d, yyyy h:mm a')}</span>
              </div>
              
              {/* Link */}
              {selectedHeadline.link_url && (
                <a 
                  href={selectedHeadline.link_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-blue-400 hover:underline"
                >
                  <ExternalLink className="h-4 w-4" />
                  View link
                </a>
              )}
              
              {/* Reactions */}
              <div className="flex items-center gap-2 pt-2 border-t border-slate-700">
                <span className="text-sm text-slate-400 mr-2">React:</span>
                {['❤️', '🎉', '👍', '🎯', '🔥'].map(emoji => (
                  <Button
                    key={emoji}
                    variant="ghost"
                    size="sm"
                    onClick={() => handleReact(emoji)}
                    className="px-2"
                  >
                    {emoji} {selectedHeadline.reactions?.filter(r => r.emoji === emoji).length || 0}
                  </Button>
                ))}
              </div>
              
              {/* Comments */}
              <div className="space-y-3 pt-2 border-t border-slate-700">
                <Label className="dark:text-slate-200">Comments ({selectedHeadline.comments?.length || 0})</Label>
                
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {selectedHeadline.comments?.map((comment, idx) => (
                    <div key={idx} className="p-2 bg-slate-700/30 rounded-lg text-sm">
                      <p className="dark:text-white">{comment.text}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {format(new Date(comment.timestamp), 'MMM d, h:mm a')}
                      </p>
                    </div>
                  ))}
                </div>
                
                {/* Add Comment */}
                <div className="flex gap-2">
                  <Input
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Add a comment..."
                    className="dark:bg-slate-700 dark:border-slate-600"
                    onKeyDown={(e) => e.key === 'Enter' && handleComment()}
                  />
                  <Button size="sm" onClick={handleComment}>
                    Post
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="flex justify-between">
            <Button 
              variant="destructive" 
              onClick={() => setDeleteDialogOpen(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="dark:bg-slate-800 dark:border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="dark:text-white">Delete Headline?</AlertDialogTitle>
            <AlertDialogDescription className="dark:text-slate-400">
              This action cannot be undone. This will permanently delete this headline.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="dark:bg-slate-700 dark:border-slate-600">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default EOSHeadlinesSlider;
